import React from "react";
import styles from "./admin.module.scss";
import cx from "./cx";

export interface TabItem<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Optional count or badge after the label. */
  extra?: React.ReactNode;
}

interface TabsProps<T extends string> {
  tabs: Array<TabItem<T>>;
  value: T;
  onChange: (value: T) => void;
  label?: string;
  className?: string;
}

function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
}: TabsProps<T>) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    const next = (index + delta + tabs.length) % tabs.length;
    refs.current[next]?.focus();
    onChange(tabs[next].value);
  };

  return (
    <div
      className={cx(styles.tabs, className)}
      role="tablist"
      aria-label={label}
    >
      {tabs.map((t, i) => {
        const on = t.value === value;
        return (
          <button
            key={t.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            className={cx(styles.tab, on && styles.tabOn)}
            onClick={() => onChange(t.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {t.label}
            {t.extra}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
